import React from 'react';
import {
  VStack, HStack, Text, Badge, BadgeText,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { Task } from '@/student/types';
import { countWords } from '../../../common/utils/wordCount';
import { isOralExamTask } from '../../../common/utils/tasks/isOralExamTask';

interface TaskStatusProps {
  task: Task;
  variant?: 'default' | 'compact';
}

/**
 * TaskStatus
 * ----------
 * Displays the current status of the task.
 *
 * Business Logic:
 * - For tasks with "hidden work" (like essays), we show the state (Draft/Done)
 *   as a summary signal of how much work exists behind the task screen.
 * - For event tasks, the state is already apparent from the session booking
 *   interface, so we omit the explicit state row to avoid redundancy.
 * - The "Done" state for event tasks indicates that the user has either
 *   finished their participation or has opted out of further sessions.
 */
export const TaskStatus: React.FC<TaskStatusProps> = ({ task, variant = 'default' }) => {
  const { t } = useTranslation();

  const state = task?.submission?.state;
  const isCompleted = state === 'marked_as_done';
  const isEssay = task?.type === 'essay';
  const isEvent = task?.type === 'event' || isOralExamTask(task);

  // Calculate word count for essays
  const wordCount = isEssay && task?.submission?.text ? countWords(task.submission.text) : 0;

  // We only show the status section if there's something meaningful to report
  // that isn't already obvious from the main task view.
  if (!state && (!isEssay || wordCount === 0)) return null;
  if (isEvent) return null;

  if (variant === 'compact') {
    return (
      <HStack space="sm" alignItems="center">
        {/* State Badge */}
        {!!state && (
          <Badge action={isCompleted ? 'success' : 'muted'} size="sm">
            <BadgeText>
              {isCompleted
                ? t('student.module.task-sidebar.done', 'Done')
                : t('student.module.task-sidebar.in-progress', 'In Progress')}
            </BadgeText>
          </Badge>
        )}

        {/* Word Count Badge (for essays) */}
        {!!(isEssay && wordCount > 0) && (
          <Badge action="muted" size="sm">
            <BadgeText>{t('student.module.task-sidebar.wordCountValue', { count: wordCount })}</BadgeText>
          </Badge>
        )}
      </HStack>
    );
  }

  return (
    <VStack space="md" mt="$8">
      <Text size="xs" color="$textHint" textTransform="uppercase" letterSpacing="$md">
        {t('student.module.task-sidebar.status', 'Status')}
      </Text>

      <HStack space="sm" flexWrap="wrap">
        {/* State Badge */}
        {!!state && (
          <Badge action={isCompleted ? 'success' : 'muted'} size="md">
            <BadgeText>
              {isCompleted
                ? t('student.module.task-sidebar.done', 'Done')
                : t('student.module.task-sidebar.in-progress', 'In Progress')}
            </BadgeText>
          </Badge>
        )}

        {/* Word Count Badge (for essays) */}
        {!!(isEssay && wordCount > 0) && (
          <Badge action="muted" size="md">
            <BadgeText>{t('student.module.task-sidebar.wordCountValue', { count: wordCount })}</BadgeText>
          </Badge>
        )}
      </HStack>
    </VStack>
  );
};

export default TaskStatus;
